/* views/Contacts.jsx — v5 logic, v6 design */

import React from 'react'
import { humanize, formatPhone, validatePhone, formatMoney, formatDate, ENUM_OPTIONS, BUSINESS_CONTACT_TYPES } from '../utils.jsx'
import { Contacts } from '../db.jsx'
import { Pill, Drawer, Field, EnumSelect, PhoneInput, EmptyState, Spinner, FilterBar, useFilteredRows, useToast, ConfirmModal } from '../components.jsx'
import { I } from '../icons.jsx'

const EMPTY_CONTACT = {
  name: "", email: "", phone: "", birthday: "", address: "",
  type: "customer", source: "", lifecycle_status: "cold_lead",
  reseller_discount_pct: "", business_name: "", tin: "",
  billing_address: "", contact_person: "",
  price_tier: "retail", payment_terms: "", credit_limit: "",
  notes: "",
};

function ContactsView() {
  const [rows, setRows] = React.useState(null);
  const [open, setOpen] = React.useState(null);
  const [search, setSearch] = React.useState("");
  const [typeFilter, setTypeFilter] = React.useState("");

  React.useEffect(() => {
    const ctrl = new AbortController();
    Contacts.list(ctrl.signal).then(r => setRows(Array.isArray(r) ? r : (r.data || [])));
    return () => ctrl.abort();
  }, []);

  const refresh = React.useCallback(() => {
    Contacts.list().then(r => setRows(Array.isArray(r) ? r : (r.data || [])));
  }, []);

  const filtered = (rows || []).filter(r => {
    if (typeFilter && r.type !== typeFilter) return false;
    if (search) {
      const s = search.toLowerCase();
      return (r.name || "").toLowerCase().includes(s)
          || (r.phone || "").includes(s)
          || (r.email || "").toLowerCase().includes(s)
          || (r.business_name || "").toLowerCase().includes(s);
    }
    return true;
  });

  return (
    <div className="view-enter">
      <div className="filter-bar">
        <div className="searchbar" style={{ marginBottom: 0, flex: "0 1 280px", minWidth: 200 }}>
          <span style={{ width: 16, height: 16, display: "grid", placeItems: "center" }}>{I.search}</span>
          <input placeholder="Search by name, phone, business…"
                 value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="select" style={{ width: "auto", minWidth: 180 }}
                value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
          <option value="">All classifications</option>
          {ENUM_OPTIONS.contact_type.map(t => <option key={t} value={t}>{humanize(t)}</option>)}
        </select>
        <span className="spacer"/>
        <button className="btn btn-primary" onClick={() => setOpen("new")}>{I.plus} New contact</button>
      </div>

      <div className="card">
        <div className="tbl-wrap">
          {rows === null ? <Spinner label="Loading contacts…" /> :
           filtered.length === 0
             ? <EmptyState title="No contacts" hint={rows.length ? "No contacts match your filters." : "Create your first contact."} />
             : <table className="tbl">
                 <thead><tr>
                   <th>Name</th><th>Type</th><th>Lifecycle</th><th>Phone</th>
                   <th>Email</th><th style={{ textAlign: "right" }}>Orders</th>
                   <th style={{ textAlign: "right" }}>Total Spent</th>
                 </tr></thead>
                 <tbody>
                   {filtered.map(r => (
                     <tr key={r.id} onClick={() => setOpen(r)}>
                       <td>
                         <div style={{ fontWeight: 600 }}>{r.name}</div>
                         {r.business_name && <div className="muted" style={{ fontSize: 11 }}>{r.business_name}</div>}
                       </td>
                       <td><Pill tone="info">{humanize(r.type)}</Pill></td>
                       <td>{r.lifecycle_status ? <Pill tone="muted">{humanize(r.lifecycle_status)}</Pill> : <span className="faint">—</span>}</td>
                       <td className="mono" style={{ fontSize: 12 }}>{r.phone || <span className="faint">—</span>}</td>
                       <td className="muted">{r.email || <span className="faint">—</span>}</td>
                       <td className="mono" style={{ textAlign: "right" }}>{r.total_orders || 0}</td>
                       <td className="amt" style={{ textAlign: "right" }}>{formatMoney(r.total_spent || 0)}</td>
                     </tr>
                   ))}
                 </tbody>
               </table>
          }
        </div>
      </div>

      {open && <ContactDrawer
        contact={open === "new" ? EMPTY_CONTACT : { ...EMPTY_CONTACT, ...open }}
        isNew={open === "new"}
        onClose={() => setOpen(null)}
        onSaved={() => { setOpen(null); refresh(); }}
      />}
    </div>
  );
}

function ContactDrawer({ contact, isNew, onClose, onSaved }) {
  const toast = useToast();
  const [f, setF] = React.useState(contact);
  const [busy, setBusy] = React.useState(false);
  const [errors, setErrors] = React.useState({});
  const [confirmDelete, setConfirmDelete] = React.useState(false);

  const showBusiness = BUSINESS_CONTACT_TYPES.includes(f.type);
  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));

  function validate() {
    const e = {};
    if (!f.name?.trim()) e.name = "Required";
    if (f.phone && !validatePhone(f.phone)) e.phone = "Invalid phone format (expected mobile or landline)";
    if (f.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email)) e.email = "Invalid email";
        if (showBusiness && f.tin) {
        const normalized = f.tin.replace(/\D/g, "");
        if (normalized.length !== 9 && normalized.length !== 12) {
          e.tin = "TIN must be 9 or 12 digits";
        }
      }
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function save() {
    if (!validate()) return;
    setBusy(true);
    const payload = { ...f };
    if (payload.phone) payload.phone = formatPhone(payload.phone);
    const res = isNew ? await Contacts.create(payload) : await Contacts.update(contact.id, payload);
    setBusy(false);
    if (!res.error) {
      toast.push({ tone: "ok", title: isNew ? "Contact created" : "Contact updated" });
      onSaved();
    }
  }

  async function remove() {
    setBusy(true);
    const res = await Contacts.remove(contact.id);
    setBusy(false);
    if (!res.error) {
      toast.push({ tone: "ok", title: "Contact deleted" });
      onSaved();
    }
  }

  const SectionLabel = ({ children }) => (
    <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--text-faint)", fontWeight: 600, marginBottom: 10, marginTop: 18, paddingBottom: 6, borderBottom: "1px solid var(--border)" }}>
      {children}
    </div>
  );

  return (
    <>
    {confirmDelete && (
      <ConfirmModal
        title="Delete contact"
        message={`Soft-delete "${contact.name}"? They can be restored later.`}
        confirmLabel="Delete"
        danger
        onConfirm={remove}
        onClose={() => setConfirmDelete(false)}
      />
    )}
    <Drawer
      title={isNew ? "New contact" : `Edit · ${contact.name}`}
      onClose={onClose}
      footer={
        <>
          {!isNew && <button className="btn btn-danger" onClick={() => setConfirmDelete(true)} disabled={busy}>{I.trash} Delete</button>}
          <span className="spacer" />
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={busy}>{busy ? "Saving…" : <>{I.check} Save</>}</button>
        </>
      }
    >
      <SectionLabel>Basic info</SectionLabel>
      <Field label="Full name" required error={errors.name}>
        <input className="input" style={errors.name ? { borderColor: "var(--danger)" } : undefined}
               value={f.name} onChange={e => set("name", e.target.value)} />
      </Field>
      <Field label="Classification" required help="Reseller/Supplier/Restaurant/Partner unlocks business fields.">
        <EnumSelect value={f.type} onChange={v => set("type", v)} options={ENUM_OPTIONS.contact_type} />
      </Field>
      <Field label="Lifecycle status">
        <EnumSelect value={f.lifecycle_status} onChange={v => set("lifecycle_status", v)} options={ENUM_OPTIONS.lifecycle_status} allowEmpty />
      </Field>
      <Field label="Source">
        <EnumSelect value={f.source} onChange={v => set("source", v)} options={ENUM_OPTIONS.contact_source} allowEmpty />
      </Field>

      <SectionLabel>Contact</SectionLabel>
      <Field label="Phone" help="Default country code +63 — paste any format, we'll normalize." error={errors.phone}>
        <PhoneInput value={f.phone} onChange={v => set("phone", v)} error={!!errors.phone} />
      </Field>
      <Field label="Email" error={errors.email}>
        <input className="input" type="email" style={errors.email ? { borderColor: "var(--danger)" } : undefined}
               value={f.email || ""} onChange={e => set("email", e.target.value)} />
      </Field>
      <Field label="Address">
        <textarea className="textarea" value={f.address || ""} onChange={e => set("address", e.target.value)} />
      </Field>
      <Field label="Birthday">
        <input className="input" type="date" value={f.birthday || ""} onChange={e => set("birthday", e.target.value)} />
      </Field>

      <SectionLabel>Pricing</SectionLabel>
      <Field label="Price tier">
        <EnumSelect value={f.price_tier} onChange={v => set("price_tier", v)} options={ENUM_OPTIONS.price_tier} />
      </Field>

      {showBusiness && (
        <>
          <SectionLabel>Business details</SectionLabel>
          <Field label="Business name">
            <input className="input" value={f.business_name || ""} onChange={e => set("business_name", e.target.value)} />
          </Field>
          <Field label="TIN" help="Format: 123-456-789 or 123-456-789-000" error={errors.tin}>
            <input className="input mono" style={errors.tin ? { borderColor: "var(--danger)" } : undefined}
                   value={f.tin || ""} onChange={e => set("tin", e.target.value)} placeholder="123-456-789-000" />
          </Field>
          <Field label="Billing address">
            <textarea className="textarea" value={f.billing_address || ""} onChange={e => set("billing_address", e.target.value)} />
          </Field>
          <Field label="Contact person">
            <input className="input" value={f.contact_person || ""} onChange={e => set("contact_person", e.target.value)} />
          </Field>
          <Field label="Reseller discount %">
            <input className="input" type="number" step="0.5" min="0" max="100"
                   value={f.reseller_discount_pct || ""} onChange={e => set("reseller_discount_pct", e.target.value)} />
          </Field>
          <Field label="Payment terms" help="e.g. Net 30, COD, 50% deposit">
            <input className="input" value={f.payment_terms || ""} onChange={e => set("payment_terms", e.target.value)} />
          </Field>
          <Field label="Credit limit (₱)">
            <input className="input" type="number" min="0"
                   value={f.credit_limit || ""} onChange={e => set("credit_limit", e.target.value)} />
          </Field>
        </>
      )}

      <SectionLabel>Notes</SectionLabel>
      <Field>
        <textarea className="textarea" value={f.notes || ""} onChange={e => set("notes", e.target.value)} />
      </Field>
    </Drawer>
    </>
  );
}

export { ContactsView };
